import { useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, SlideOut, SlideOutHeader } from '@aimsos/ui';

import NextBestActionActionButton from './NextBestActionActionButton.jsx';
import NextBestActionDetails from './NextBestActionDetails.jsx';
import NextBestActionJourney from './NextBestActionJourney.jsx';
import NextBestActionOtherList from './NextBestActionOtherList.jsx';

import { Sparkles } from 'lucide-react';

/**
 * The Next Best Action slide-out (CT-5713), in one of two modes:
 *
 *   details   "View details": Source, Why, Ways to do it, Steps; the footer
 *             repeats the suggestion's primary action
 *   journey   "Review workflow": The journey · N steps · time, When you start
 *             it; the footer is Start workflow
 *
 * Both end with the other recommendations. Overlay, focus and Escape are
 * `SlideOut`'s. A failed start keeps the panel open (the hook toasts).
 */
function NextBestActionSlideOut({ item, mode, items, entity, starting, onAction, onStartWorkflow, onClose }) {
  const { t } = useTranslation('unified-profiles');

  const others = useMemo(() => items.filter((candidate) => candidate.id !== item.id), [items, item.id]);
  const handleStart = useCallback(() => onStartWorkflow(item), [onStartWorkflow, item]);
  const title = t('profile.nba.label');

  const renderHeader = useCallback(
    (titleId) => (
      <SlideOutHeader
        titleId={titleId}
        title={title}
        subtitle={t('profile.nba.moreRecommendations', { count: others.length })}
        icon={Sparkles}
        size="M"
        showClose
        onClose={onClose}
      />
    ),
    [title, others.length, onClose, t],
  );

  const journey = mode === 'journey' && item.workflow;

  return (
    <SlideOut isOpen onClose={onClose} renderHeader={renderHeader} size="M" ariaLabel={title}>
      <div data-testid="nba-slideout" data-mode={journey ? 'journey' : 'details'} className="flex h-full min-h-0 flex-col gap-4">
        <div className="scrollbar-aimsos-auto flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold text-content">{item.title}</span>
            <span className="text-sm text-content-secondary">{[item.context, item.description].filter(Boolean).join(' ')}</span>
          </div>
          {journey ? (
            <NextBestActionJourney workflow={item.workflow} />
          ) : (
            <NextBestActionDetails item={item} entity={entity} onAction={onAction} />
          )}
          <NextBestActionOtherList items={others} entity={entity} onAction={onAction} />
        </div>

        <div className="flex shrink-0 justify-end border-t border-edge pt-4">
          {journey ? (
            <Button variant="primary" size="sm" onClick={handleStart} disabled={starting}>
              {t('profile.nba.startWorkflow')}
            </Button>
          ) : (
            <NextBestActionActionButton item={item} action={item.primaryAction} entity={entity} onAction={onAction} />
          )}
        </div>
      </div>
    </SlideOut>
  );
}

export default NextBestActionSlideOut;
